import { getStatusForId as getStatus } from '@ms/yammer-data-support';
import { GroupMemberRole, GroupMemberRoleFilter } from '@ms/yammer-graphql';

import { EntityStatus, Group, GroupRoleMembers, User, YammerState } from '../../state/types';
import { createFindById } from '../findById';
import { getGroupMembers } from '../groupMemberPanels/selectors';
import { getGroupMemberSearchResults } from '../groupMemberSearch/selectors';

export const groupMemberRoleFilterMap: Record<GroupMemberRole, GroupMemberRoleFilter> = {
  [GroupMemberRole.EXPERT]: GroupMemberRoleFilter.EXPERTS,
  [GroupMemberRole.EXPERT_NOMINEE]: GroupMemberRoleFilter.EXPERT_NOMINEES,
  [GroupMemberRole.LEADER]: GroupMemberRoleFilter.LEADERS,
};

export type GroupAdminMap = Record<
  User['id'],
  {
    readonly id: User['id'];
    readonly isAdmin: boolean;
  }
>;

export const findById = createFindById('groupRoleMembers');

type GetStatusForId = (state: YammerState, groupId: Group['id'], roleFilter: GroupMemberRoleFilter) => EntityStatus;
export const getStatusForId: GetStatusForId = (state, groupId, roleFilter) =>
  getStatus(state.groupRoleMembers, getGroupRoleMemberKey(groupId, roleFilter));

type GetGroupRoleMemberKey = (groupId: Group['id'], roleFilter: GroupMemberRoleFilter) => string;
export const getGroupRoleMemberKey: GetGroupRoleMemberKey = (groupId, roleFilter) => `${groupId}-${roleFilter}`;

const emptyGroupRoleMembers: ReturnType<GetGroupRoleMembers> = [];
type GetGroupRoleMembers = (
  state: YammerState,
  groupId: Group['id'],
  roleFilter: GroupMemberRoleFilter
) => GroupRoleMembers['roleMembers'];
export const getGroupRoleMembers: GetGroupRoleMembers = (state, groupId, roleFilter) =>
  findById(state, getGroupRoleMemberKey(groupId, roleFilter))?.roleMembers || emptyGroupRoleMembers;

type GetFilteredGroupRoleMembers = (
  state: YammerState,
  groupId: Group['id'],
  roleFilter: GroupMemberRoleFilter
) => GroupAdminMap;
export const getFilteredGroupRoleMembers: GetFilteredGroupRoleMembers = (state, groupId, roleFilter) => {
  const searchMembers = getGroupMemberSearchResults(state, groupId);
  const groupRoleMembers = getGroupRoleMembers(state, groupId, roleFilter);

  return groupRoleMembers.reduce<GroupAdminMap>((acc, { nodeId, isAdmin }) => {
    if (searchMembers.some(({ id }) => id === nodeId)) {
      return {
        ...acc,
        [nodeId]: { id: nodeId, isAdmin },
      };
    }

    return acc;
  }, {});
};

type GetGroupRoleMembersMap = (state: YammerState, groupId: Group['id'], role: GroupMemberRoleFilter) => GroupAdminMap;
export const getGroupRoleMembersMap: GetGroupRoleMembersMap = (state, groupId, role) =>
  getGroupRoleMembers(state, groupId, role).reduce<ReturnType<typeof getGroupRoleMembersMap>>(
    (acc, { nodeId: id, isAdmin }) => ({
      ...acc,
      [id]: { id, isAdmin },
    }),
    {}
  );

type HasGroupRoleMembers = (state: YammerState, groupId: Group['id'], role: GroupMemberRoleFilter) => boolean;
export const hasGroupRoleMembers: HasGroupRoleMembers = (state, groupId, role) => {
  const groupRoleMembers = getGroupRoleMembersMap(state, groupId, role);

  return Object.keys(groupRoleMembers).length > 0;
};

type hasUserGroupRole = (
  state: YammerState,
  roleFilter: GroupMemberRoleFilter,
  groupId: Group['id'],
  userId: User['id']
) => boolean;
export const hasUserGroupRole: hasUserGroupRole = (state, roleFilter, groupId, userId) =>
  getGroupRoleMembers(state, groupId, roleFilter).some((member) => member.nodeId === userId);

type GetGroupNonRoleMembers = (
  state: YammerState,
  groupId: Group['id'],
  roles: GroupMemberRole[]
) => ReturnType<typeof getGroupMembers>;
export const getGroupNonRoleMembers: GetGroupNonRoleMembers = (state, groupId, roles) => {
  const groupMembers = getGroupMembers(state, groupId);
  const roleMembers = roles.reduce((acc, role) => {
    const roleMembers = getGroupRoleMembers(state, groupId, groupMemberRoleFilterMap[role]);

    return [...acc, ...roleMembers];
  }, []);

  return groupMembers.filter((member) => !roleMembers.some((roleMember) => roleMember.nodeId === member.id));
};
